/**
 * HouseDetailPage - Full property detail view with gallery and image management.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import APIAgent from '../../agents/apiAgent';
import { useFavorites } from '../../agents/favoritesAgent';
import { useTourRequests } from '../../agents/tourRequestAgent';
import TourRequestModal from '../Tours/TourRequestModal';
import ImageGallery from './ImageGallery';
import RoomTable from './RoomTable';
import { MLS_SECTIONS, toLabel } from '../SellerDashboard/mlsFieldSchema';
import { getTopFeatureHighlights } from '../../utils/propertyFeatures';
import BathBreakdownBadge from '../ui/BathBreakdownBadge';
import Badge from '../ui/Badge';
import OwnershipReviewedBadge from '../OwnershipReviewedBadge/OwnershipReviewedBadge';
import ConditionReportButton, { hasConditionReport } from '../ConditionReport/ConditionReportButton';
import { describeSale, isOfferable, isSold, isTourable as isTourableStatus } from '../../utils/listingStatus';
import './HouseDetailPage.css';

const hasMlsValue = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const formatMlsValue = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};


const getProfileSections = (mlsProfile) => {
  if (!mlsProfile || typeof mlsProfile !== 'object') return [];

  return MLS_SECTIONS
    .map((section) => {
      const fields = section.fields
        .map((field) => {
          const rawValue = mlsProfile[field.key];
          if (!hasMlsValue(rawValue)) return null;

          return {
            key: field.key,
            label: field.label || toLabel(field.key),
            value: formatMlsValue(rawValue),
          };
        })
        .filter(Boolean);

      return fields.length > 0
        ? {
            key: section.key,
            title: section.title,
            icon: section.icon,
            fields,
          }
        : null;
    })
    .filter(Boolean);
};

const HouseDetailPage = ({ houseId, buyerId, onBack, onMakeOffer, autoOpenTour = false, onTourAutoOpenHandled }) => {
  const [house, setHouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [tourModalOpen, setTourModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const { addFavorite, removeFavorite, fetchFavorites, isFavorite } = useFavorites(buyerId);
  useTourRequests(buyerId);

  const isFavorited = house ? isFavorite(house.id) : false;

  const fetchHouse = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.getHouseById(houseId);
      setHouse(data);
    } catch (err) {
      console.error('Error fetching house:', err);
      setError(err.message || 'Failed to load property details');
    } finally {
      setLoading(false);
    }
  }, [houseId]);

  useEffect(() => {
    fetchHouse();
  }, [fetchHouse]);

  useEffect(() => {
    if (buyerId) fetchFavorites();
  }, [buyerId, fetchFavorites]);

  // Auto-open the tour modal once the house has loaded (e.g. when navigated
  // here via the "Schedule a Tour" CTA on the Transaction Journey).
  useEffect(() => {
    if (!autoOpenTour || !house || tourModalOpen) return;
    // QA T-29: never auto-open the modal for a listing the backend will reject.
    // Nor for the seller's own listing (the backend answers 409 `own_listing`).
    const ownListing = buyerId != null && house.seller_id === buyerId;
    if (!isTourableStatus(house.listing_status) || ownListing) {
      if (onTourAutoOpenHandled) onTourAutoOpenHandled();
      return;
    }
    setTourModalOpen(true);
    if (onTourAutoOpenHandled) onTourAutoOpenHandled();
  }, [autoOpenTour, house, tourModalOpen, onTourAutoOpenHandled, buyerId]);

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    try {
      setUploading(true);
      setUploadError(null);
      const result = await APIAgent.uploadHouseImages(houseId, files);
      setHouse(prev => ({ ...prev, image_urls: result.image_urls }));
    } catch (err) {
      console.error('Error uploading images:', err);
      setUploadError(err.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageIndex) => {
    try {
      const result = await APIAgent.deleteHouseImage(houseId, imageIndex);
      setHouse(prev => ({ ...prev, image_urls: result.image_urls }));
    } catch (err) {
      console.error('Error deleting image:', err);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) handleUploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleFileSelect = (e) => {
    handleUploadFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const toggleFavorite = async () => {
    if (!house) return;
    try {
      if (isFavorited) {
        await removeFavorite(house.id);
      } else {
        await addFavorite(house.id, `Interested in property at ${house.address}`);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  if (loading) {
    return (
      <div className="detail-page">
        <div className="detail-skeleton" aria-busy="true" aria-label="Loading property details">
          <div className="detail-skeleton__topbar" />
          <div className="detail-skeleton__gallery" />
          <div className="detail-skeleton__content">
            <div className="detail-skeleton__main">
              <div className="detail-skeleton__line detail-skeleton__line--wide" />
              <div className="detail-skeleton__line detail-skeleton__line--medium" />
              <div className="detail-skeleton__line detail-skeleton__line--price" />
              <div className="detail-skeleton__stats" />
              <div className="detail-skeleton__line" />
              <div className="detail-skeleton__line" />
              <div className="detail-skeleton__line detail-skeleton__line--short" />
            </div>
            <div className="detail-skeleton__sidebar" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail-page">
        <div className="detail-error">
          <p>{error}</p>
          <button onClick={onBack}>← Back to listings</button>
        </div>
      </div>
    );
  }

  if (!house) return null;

  const images = Array.isArray(house.image_urls) ? house.image_urls : [];
  const profileSections = getProfileSections(house.mls_profile);
  const topFeatureHighlights = getTopFeatureHighlights(house.mls_profile, 5);
  // The viewer is the listing's own seller: they manage photos and cannot
  // make an offer on their own property.
  const isOwnListing =
    buyerId != null && house.seller_id != null && buyerId === house.seller_id;
  // QA T-29: only `active` / `pending` listings accept tour requests. Mirrors
  // TOURABLE_LISTING_STATUSES in backend/app/services/listing_visibility.py —
  // the backend still enforces it with a 409, this just avoids offering a CTA
  // that is guaranteed to fail (e.g. on a `sold` listing, which stays viewable).
  const isTourable = isTourableStatus(house.listing_status);
  // Same gate for offers (OFFERABLE_LISTING_STATUSES): a sold or withdrawn
  // home answers a new offer with a 409 `listing_not_offerable`.
  const canOffer = isOfferable(house.listing_status);
  const sold = isSold(house.listing_status);

  return (
    <div className="detail-page">
      {/* Top bar */}
      <div className="detail-topbar">
        <button className="detail-back-btn" onClick={onBack} aria-label="Back to listings">
          <span aria-hidden="true">←</span> Back to listings
        </button>
        <div className="detail-topbar-actions">
          <button
            className={`detail-fav-btn ${isFavorited ? 'favorited' : ''}`}
            onClick={toggleFavorite}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <span aria-hidden="true">{isFavorited ? '❤️' : '🤍'}</span>
            {isFavorited ? ' Saved' : ' Save'}
          </button>
          {isTourable && !isOwnListing && (
            <button
              className="detail-tour-btn"
              onClick={() => setTourModalOpen(true)}
              aria-label="Request a tour"
            >
              <span aria-hidden="true">🎫</span> Request Tour
            </button>
          )}
          {onMakeOffer && !isOwnListing && canOffer && (
            <button
              className="detail-offer-btn"
              onClick={() => onMakeOffer(houseId)}
              aria-label="Make an offer on this property"
            >
              <span aria-hidden="true">📄</span> Make an Offer
            </button>
          )}
        </div>
      </div>

      {/* Gallery */}
      <div className="detail-gallery-section">
        <ImageGallery
          images={images}
          onDeleteImage={handleDeleteImage}
          canManage={isOwnListing}
        />
      </div>

      {/* Image upload area — visible to the listing seller only */}
      {isOwnListing && <div
        className={`detail-upload-zone ${dragOver ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        aria-label="Upload property photos"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className="detail-upload-input"
          id="image-upload"
        />
        {uploading ? (
          <p className="upload-status">Uploading...</p>
        ) : (
          <>
            <p className="upload-label">📷 Drag &amp; drop photos here or</p>
            <button
              className="upload-browse-btn"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              Browse Files
            </button>
            <p className="upload-hint">JPEG, PNG, WebP, GIF · Max 10 MB each</p>
          </>
        )}
        {uploadError && <p className="upload-error">{uploadError}</p>}
      </div>}

      {/* Property details */}
      <div className="detail-content">
        <div className="detail-main">
          <h1 className="detail-address">{house.address}</h1>
          <p className="detail-location">
            {house.listing_number && <span className="listing-badge listing-badge--lg">Listing #{house.listing_number}</span>}
            {house.city}, {house.state} {house.zip_code}
          </p>

          <div className="detail-price-row">
            {sold && <Badge variant="listing" value="sold" />}
            <span className="detail-price">${house.price?.toLocaleString() || 'N/A'}</span>
            {house.square_feet && house.price && (
              <span className="detail-price-sqft">
                ${Math.round(house.price / house.square_feet)}/sqft
              </span>
            )}
          </div>

          {house.ownership_documents_reviewed === true && (
            <div className="detail-trust-row">
              <OwnershipReviewedBadge
                reviewed
                reviewedMonth={house.ownership_reviewed_month}
              />
            </div>
          )}

          {/* The seller's signed condition report, readable before an offer.
              This page is only reached signed in, so it opens directly. */}
          {hasConditionReport(house.condition_report) && (
            <div className="detail-condition-report">
              <ConditionReportButton
                houseId={house.id}
                conditionReport={house.condition_report}
                signedIn
              />
            </div>
          )}

          {sold && (
            <p className="detail-sold-notice" role="status">
              {describeSale(house)}. This home is no longer accepting offers or tours.
            </p>
          )}

          {topFeatureHighlights.length > 0 && (
            <div className="detail-feature-strip" aria-label="Top property features">
              {topFeatureHighlights.map((feature) => (
                <span className="detail-feature-pill" key={feature}>{feature}</span>
              ))}
            </div>
          )}

          {/* Beds, baths and living area describe a home; vacant land has none
              of them, so the row only showed dashes and "0 Baths". */}
          {house.paperwork_category !== 'vacant_land' && (
          <div className="detail-stats" data-testid="detail-stats">
            <div className="detail-stat">
              <span className="detail-stat-value">{house.bedrooms || '—'}</span>
              <span className="detail-stat-label">Beds</span>
            </div>
            <div className="detail-stat-divider" />
            <div className="detail-stat">
              {house.full_baths != null ||
              house.three_quarter_baths != null ||
              house.half_baths != null ? (
                <>
                  <BathBreakdownBadge
                    full={house.full_baths}
                    threeQuarter={house.three_quarter_baths}
                    half={house.half_baths}
                    fallbackTotal={house.bathrooms}
                  />
                  <span className="detail-stat-label">Baths</span>
                </>
              ) : (
                <>
                  <span className="detail-stat-value">{house.bathrooms || '—'}</span>
                  <span className="detail-stat-label">Baths</span>
                </>
              )}
            </div>
            <div className="detail-stat-divider" />
            <div className="detail-stat">
              <span className="detail-stat-value">{house.square_feet?.toLocaleString() || '—'}</span>
              <span className="detail-stat-label">Sqft</span>
            </div>
          </div>
          )}

          {house.description && (
            <div className="detail-description">
              <h2>About This Property</h2>
              <p>{house.description}</p>
            </div>
          )}

          {/* Structured room inventory — hidden when the listing has no
              rooms[] (legacy listings) per design spec §3.3. */}
          {Array.isArray(house.rooms) && house.rooms.length > 0 && (
            <RoomTable rooms={house.rooms} />
          )}

          {profileSections.length > 0 && (
            <div className="detail-mls-sections">
              <h2>Property Features &amp; Details</h2>
              <div className="detail-mls-grid">
                {profileSections.map((section) => (
                  <section className="detail-mls-card" key={section.key}>
                    <h3>
                      <span className="detail-mls-icon" aria-hidden="true">{section.icon}</span>
                      {section.title}
                    </h3>
                    <dl className="detail-mls-list">
                      {section.fields.map((field) => (
                        <div className="detail-mls-item" key={field.key}>
                          <dt>{field.label}</dt>
                          <dd>{field.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="detail-sidebar">
          <div className="detail-info-card">
            <h3>Property Details</h3>
            <div className="detail-info-grid">
              {house.year_built && (
                <div className="info-item">
                  <span className="info-label">Year Built</span>
                  <span className="info-value">{house.year_built}</span>
                </div>
              )}
              {house.property_type && (
                <div className="info-item">
                  <span className="info-label">Property Type</span>
                  <span className="info-value">{house.property_type}</span>
                </div>
              )}
              {house.listing_status && (
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className="info-value">{house.listing_status}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Photos</span>
                <span className="info-value">{images.length} photos</span>
              </div>
              <div className="info-item">
                <span className="info-label">Listed</span>
                <span className="info-value">
                  {house.created_at ? new Date(house.created_at).toLocaleDateString() : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tour modal */}
      {tourModalOpen && (
        <TourRequestModal
          buyerId={buyerId}
          houseId={house.id}
          houseName={house.address}
          onClose={() => setTourModalOpen(false)}
          onSuccess={() => setTourModalOpen(false)}
        />
      )}
    </div>
  );
};

export default HouseDetailPage;
